import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";

const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtEur = (n) => `€${Number(n || 0).toFixed(2)}`;
const fmtCdf = (n) => `${Math.round(Number(n || 0)).toLocaleString("fr-FR")} FC`;

export default function PSQuoteDetail() {
  const { qid } = useParams();
  const { t, lang } = useLang();
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!qid) return;
    api.get(`/personal-shopper/quotes/${qid}/public`)
      .then((r) => setQuote(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Devis introuvable"));
  }, [qid]);

  if (err) return <div className="p-12 text-center text-[#4B5563]" data-testid="psq-detail-error">{err}</div>;
  if (!quote) return <div className="p-12 text-center text-[#4B5563]">Chargement…</div>;

  const localeDate = lang === "en" ? "en-GB" : "fr-FR";
  const dateStr = new Date(quote.created_at).toLocaleDateString(localeDate, { day: "2-digit", month: "long", year: "numeric" });
  const isFrance = quote.delivery_country === "france";

  return (
    <div className="max-w-3xl mx-auto p-6 print:p-0" data-testid="psq-detail-page">
      <div className="print:hidden mb-6 flex gap-2 justify-between flex-wrap">
        <Button variant="ghost" onClick={() => window.history.back()} className="rounded-full">
          <ArrowLeft className="w-4 h-4 mr-1"/> Retour
        </Button>
        <Button onClick={() => window.print()} className="pill-btn bg-[#1A3322] hover:bg-[#24472F] rounded-full" data-testid="psq-detail-print">
          <Printer className="w-4 h-4 mr-2"/> Imprimer / Enregistrer en PDF
        </Button>
      </div>

      <div className="bg-white border border-[#1A3322]/15 rounded-lg p-10 print:border-0 print:rounded-none">
        {/* Header */}
        <div className="flex justify-between items-start mb-10 flex-wrap gap-4">
          <div>
            <img
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_sukana-launch/artifacts/9b5fhq23_LOGO%20SUKACOOP.PNG"
              alt="SUKANACOOP"
              className="h-14 mb-3"
            />
            <p className="heading font-extrabold text-2xl text-[#1A3322]">SUKANACOOP</p>
            <p className="text-xs text-[#4B5563] mt-1">Personal Shopper — service opéré par</p>
            <p className="text-sm font-semibold mt-1">GNL SERVICES ET CONSULTING SARL</p>
            <p className="text-[10px] text-[#4B5563]">RCCM CD/KNG/RCCM/24-B-04135</p>
          </div>
          <div className="text-right">
            <h1 className="heading font-extrabold text-3xl text-[#1A3322]">DEVIS</h1>
            <p className="text-sm text-[#4B5563] mt-1">N° <span className="font-mono font-bold text-[#C85A32]" data-testid="psq-detail-number">{quote.quote_number}</span></p>
            <p className="text-sm text-[#4B5563]">Émis le <span className="font-semibold text-[#111812]">{dateStr}</span></p>
            <p className="text-xs text-[#4B5563] mt-1">Valable 7 jours</p>
          </div>
        </div>

        {/* Client */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#4B5563] font-bold mb-2">Client</p>
            <p className="font-bold text-[#111812]">{quote.full_name}</p>
            <p className="text-sm text-[#4B5563]">{quote.email}</p>
            {quote.phone && <p className="text-sm text-[#4B5563]">{quote.phone}</p>}
            {quote.country && <p className="text-sm text-[#4B5563]">{quote.country}</p>}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#4B5563] font-bold mb-2">Prestation</p>
            <p className="font-bold text-[#111812]">Personal Shopper</p>
            <p className="text-sm text-[#4B5563]">Scénario : {quote.scenario === "fr_to_cd" ? "🇫🇷 France → 🇨🇩 Congo" : quote.scenario}</p>
            <p className="text-sm text-[#4B5563]">Destination : {isFrance ? "🇫🇷 France" : `🇨🇩 Kinshasa (Zone ${quote.kinshasa_zone || "—"})`}</p>
            <p className="text-sm text-[#4B5563]">Poids : {Number(quote.weight_kg || 0).toFixed(2)} kg</p>
          </div>
        </div>

        {/* Detail table */}
        <div className="mb-8">
          <p className="text-[10px] uppercase tracking-widest text-[#4B5563] font-bold mb-3">Détail du calcul</p>
          <table className="w-full text-sm border border-[#1A3322]/15 rounded overflow-hidden">
            <thead>
              <tr className="bg-[#F5F5F0] text-[#1A3322]">
                <th className="text-left py-2 px-3 font-bold">Ligne</th>
                <th className="text-right py-2 px-3 font-bold">Montant (USD)</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-[#1A3322]/10">
                <td className="py-2.5 px-3">Achats produit</td>
                <td className="py-2.5 px-3 text-right font-semibold">{fmtUsd(quote.purchase_amount_usd)}</td>
              </tr>
              <tr className="border-t border-[#1A3322]/10">
                <td className="py-2.5 px-3">Commission SUKANACOOP ({Number(quote.commission_pct).toFixed(0)}%)</td>
                <td className="py-2.5 px-3 text-right font-semibold">{fmtUsd(quote.commission_usd)}</td>
              </tr>
              <tr className="border-t border-[#1A3322]/10">
                <td className="py-2.5 px-3">Fret ({Number(quote.freight_per_kg_usd).toFixed(2)}$/kg × {Number(quote.weight_kg || 0).toFixed(2)} kg)</td>
                <td className="py-2.5 px-3 text-right font-semibold">{fmtUsd(quote.freight_usd)}</td>
              </tr>
              <tr className="border-t border-[#1A3322]/10">
                <td className="py-2.5 px-3">
                  {isFrance
                    ? `Livraison France (${quote.france_freight_per_kg_usd != null ? Number(quote.france_freight_per_kg_usd).toFixed(2) + "$/kg" : "à définir"})`
                    : `Livraison Kinshasa Zone ${quote.kinshasa_zone}${quote.delivery_cdf ? ` — ${fmtCdf(quote.delivery_cdf)}` : ""}`}
                </td>
                <td className="py-2.5 px-3 text-right font-semibold">{fmtUsd(quote.delivery_usd)}</td>
              </tr>
              <tr className="bg-[#1A3322] text-[#D98A2C]">
                <td className="py-3 px-3 font-extrabold uppercase tracking-wider">Total</td>
                <td className="py-3 px-3 text-right font-extrabold text-lg" data-testid="psq-detail-total">{fmtUsd(quote.total_usd)}</td>
              </tr>
              <tr className="bg-[#F5F5F0] text-xs text-[#4B5563]">
                <td className="py-2 px-3">Équivalent EUR</td>
                <td className="py-2 px-3 text-right">{fmtEur(quote.total_eur)}</td>
              </tr>
              <tr className="bg-[#F5F5F0] text-xs text-[#4B5563]">
                <td className="py-2 px-3">Équivalent CDF</td>
                <td className="py-2 px-3 text-right">{fmtCdf(quote.total_cdf)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {quote.notes && (
          <div className="mb-8 p-4 bg-[#FFF7ED] border-l-4 border-[#C85A32] rounded">
            <p className="text-[10px] uppercase tracking-widest text-[#C85A32] font-bold mb-1">Note du client</p>
            <p className="text-sm whitespace-pre-line text-[#111812]">{quote.notes}</p>
          </div>
        )}

        {/* Terms */}
        <div className="border-t border-[#1A3322]/15 pt-4 text-[11px] text-[#4B5563] leading-relaxed">
          <p className="font-bold text-[#111812] mb-1">Conditions</p>
          <ul className="list-disc pl-4 space-y-0.5">
            <li>Devis indicatif — le prix final sera confirmé sous 24 à 48h après vérification de la disponibilité du produit.</li>
            <li>Aucun paiement n'est requis à la génération du devis.</li>
            <li>Le devis est valable 7 jours à compter de sa date d'émission.</li>
            <li>Les prix incluent la commission SUKANACOOP, le fret et la livraison à l'adresse indiquée.</li>
            <li>Contact : contact@sukanacoop.com · Kinshasa, RDC · France</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
